import {Button} from "antd";
import React, {useEffect, useRef, useState} from "react";
import {getSiteManagerList} from "../../api/getSiteManagerList";
import useUserContext from "../../hooks/useUserContext";
import {removeShopManager} from "../../api/removeSiteManager";
import {ToastSkin} from "../../context/ToastContext";
import useToast from "../../hooks/useToast";

import "./shop-managers.css";
import NewShopManagerModal from "./NewShopManagerModal";
import Skeleton from "../skeleton/Skeleton";
import CollapsableBox from "../collapsableBox/CollapsableBox";

interface ShopManagersProps {
    showTitle?: boolean;
    isInitiallyOpen?: boolean;
}

const ShopManagers = ({ showTitle, isInitiallyOpen = false }: ShopManagersProps) => {
    const [managerList, setManagerList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const { selectedDomain, user } = useUserContext();
    const { showToast, closeToast } = useToast();
    const [isNewManagerModalOpen, setIsNewManagerModalOpen] = useState(false);
    const sectionRef = useRef<HTMLDivElement | null>(null);

    const getManagers = async () => {
        if(selectedDomain) {
            setIsLoading(true);
            const response = await getSiteManagerList(selectedDomain);
            setManagerList(response.data);
            setIsLoading(false);
        }
    }

    const onDeleteManager = (userId: number) => async () => {
        if(selectedDomain) {
            const response = await removeShopManager(userId, selectedDomain);
            if(response.data === 'ok') {
                closeToast();
                showToast({ skin: ToastSkin.approval, text: 'The user was removed successfully' });
                setManagerList(managerList.filter((manager: any) => manager.id !== userId));
            }
        } else {
            closeToast();
        }

    }

    const onDeleteManagerClick = (userId: number) => async () => {
        showToast({
            skin: ToastSkin.danger,
            text: 'Are you sure you want to remove this site manager? This is irreversible',
            displayTime: 10000,
            buttons: [
                {text: 'Yes', onClick: onDeleteManager(userId)},
                {text: 'No, Cancel', onClick: closeToast}
            ]
        });
    }

    useEffect(() => {
        getManagers();
    }, [selectedDomain]);

    useEffect(() => {
        if(isInitiallyOpen) {
            sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [isInitiallyOpen]);

    const openNewManagerModal = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setIsNewManagerModalOpen(true);
    }

    const closeNewManagerModal = () => {
        setIsNewManagerModalOpen(false);
    }

    const isRealUser = user?.email !== 'external@test.com';

    return (
        <CollapsableBox title="shop managers" isInitiallyOpen={isInitiallyOpen}
            actions={isRealUser ? <Button type="primary" size="large" onClick={openNewManagerModal}>Add Manager</Button> : null}
        >
            <div ref={sectionRef} className="shop-managers-container">
                {isLoading ?
                    <Skeleton type='block' />
                    : <div className="manager-list">
                        {managerList.map((manager: any) =>
                            <div key={manager.id} className="manager-record">
                                <div className="name">{manager.name} {manager.email === user?.email ? <span>(You)</span> : ''}</div>
                                <div className="email">{manager.name.includes(selectedDomain) ? 'Shopify Automated User' : manager.email}</div>
                                <div className="actions">
                                    {manager.name.includes(selectedDomain) || manager.email === user?.email || !isRealUser ? null : <Button onClick={onDeleteManagerClick(manager.id)}>Delete</Button>}
                                </div>
                            </div>
                        )}
                    </div>
                }
                <NewShopManagerModal isOpen={isNewManagerModalOpen} onClose={closeNewManagerModal} reloadManagers={getManagers} />
            </div>
        </CollapsableBox>
    )
}

export default ShopManagers;